/**
 * StalledQuoteImport — CSV upload UI for migrating old Proline quotes
 * into Tides Hub as "Stalled Roof Quote" projects.
 *
 * Flow:
 *   1. User (or Claude) prepares a CSV using the downloadable template
 *   2. User uploads, hits "Preview" first (dry_run=true) to see per-row
 *      status without touching the DB
 *   3. If the preview looks clean, hits "Import for real" to commit +
 *      schedule the SRQ rescue ladder on each row
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Header } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Upload, Download, Loader2, CheckCircle2, AlertCircle, FileWarning, ArrowLeft } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function StalledQuoteImport() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [committed, setCommitted] = useState(null);
  const [repairing, setRepairing] = useState(false);

  const repairContacts = async () => {
    if (!window.confirm("Fix contacts imported before the schema fix landed? This coerces preferred_contact to a list and backfills missing names. Safe to run more than once.")) return;
    setRepairing(true);
    try {
      const res = await axios.post(`${API}/imports/stalled-roof-quotes/repair-contacts`);
      toast.success(`Repaired: ${res.data.preferred_contact_coerced} preferred_contact, ${res.data.names_backfilled} names`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Repair failed");
    } finally {
      setRepairing(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/imports/stalled-roof-quotes/template`);
      const blob = new Blob([res.data.csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.data.filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (e) {
      toast.error("Could not download template");
    }
  };

  const runImport = async (dryRun) => {
    if (!file) { toast.error("Pick a CSV first"); return; }
    setBusy(true);
    setPreview(null);
    setCommitted(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(
        `${API}/imports/stalled-roof-quotes?dry_run=${dryRun ? "true" : "false"}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      if (dryRun) {
        setPreview(res.data);
        toast.success(`Preview ready. ${res.data.created} would import, ${res.data.failed} would fail.`);
      } else {
        setCommitted(res.data);
        toast.success(`Imported ${res.data.created} projects. Rescue sequence scheduled.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const report = committed || preview;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => navigate(-1)} data-testid="import-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Import Stalled Roof Quotes</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Bulk migrate unsigned quotes from Proline. Every row starts the 22 day Rescue sequence.
            </p>
          </div>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Download className="w-4 h-4 text-[#0F4C81]" />
              Step 1. Download the template
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-slate-600">
              Fill in the columns exactly as shown. Aliases are accepted (phone_number, address, zip_code, etc.) but the safe path is to keep the header names identical.
            </p>
            <Button onClick={downloadTemplate} variant="outline" data-testid="import-download-template">
              <Download className="w-4 h-4 mr-2" />Download CSV template
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Upload className="w-4 h-4 text-[#0F4C81]" />
              Step 2. Upload and preview
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <label className="block">
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setCommitted(null); }}
                className="block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#0F4C81] file:text-white file:font-medium file:hover:bg-[#0D4270] file:cursor-pointer"
                data-testid="import-file-input"
              />
            </label>
            {file && (
              <p className="text-xs text-slate-500">
                Selected: <span className="font-medium">{file.name}</span> ({(file.size / 1024).toFixed(1)} KB)
              </p>
            )}
            <div className="flex flex-wrap gap-2 pt-1">
              <Button onClick={() => runImport(true)} disabled={!file || busy} variant="outline" data-testid="import-preview-btn">
                {busy && preview === null && committed === null ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Preview (dry run)
              </Button>
              <Button onClick={() => runImport(false)} disabled={!file || busy || !preview} className="bg-emerald-600 hover:bg-emerald-700" data-testid="import-commit-btn">
                {busy && committed === null ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Import for real
              </Button>
            </div>
            {!preview && !committed && (
              <p className="text-xs text-slate-400 pt-1">
                Preview first. The "Import for real" button unlocks after a preview so you never commit a bad file by accident.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Repair tool — for contacts imported before the schema fix landed. */}
        <Card className="border-amber-200 bg-amber-50/40">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-amber-900">
              <AlertCircle className="w-4 h-4" />
              Missing names on old imports?
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-xs text-slate-600">
              If you imported before this fix landed, some contacts may show without names in Pipeline. Run repair once to backfill them. Safe to run again.
            </p>
            <Button onClick={repairContacts} disabled={repairing} variant="outline" size="sm" data-testid="import-repair-contacts">
              {repairing ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : null}
              Repair contact records
            </Button>
          </CardContent>
        </Card>

        {report && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                {committed ? (
                  <><CheckCircle2 className="w-4 h-4 text-emerald-600" />Imported</>
                ) : (
                  <><FileWarning className="w-4 h-4 text-amber-600" />Preview</>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 py-2.5">
                  <p className="text-xs text-emerald-700 font-medium">Ready</p>
                  <p className="text-2xl font-bold text-emerald-900 tabular-nums">{report.created}</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 py-2.5">
                  <p className="text-xs text-slate-600 font-medium">Skipped</p>
                  <p className="text-2xl font-bold text-slate-800 tabular-nums">{report.skipped}</p>
                </div>
                <div className="rounded-lg border border-rose-200 bg-rose-50 py-2.5">
                  <p className="text-xs text-rose-700 font-medium">Errors</p>
                  <p className="text-2xl font-bold text-rose-900 tabular-nums">{report.failed}</p>
                </div>
              </div>
              <div className="max-h-[70vh] overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
                {report.results.map((r, i) => (
                  <div key={i} className="px-3 py-2.5 text-xs" data-testid={`import-row-${r.row}`}>
                    <div className="flex items-center gap-2">
                      <span className="tabular-nums text-slate-400 w-9 flex-shrink-0">L{r.row}</span>
                      {r.status === "created" && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />}
                      {r.status === "skipped" && <FileWarning className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />}
                      {r.status === "error" && <AlertCircle className="w-3.5 h-3.5 text-rose-600 flex-shrink-0" />}
                      <span className="flex-1 font-semibold text-slate-800 truncate">
                        {r.name || r.reason || ""}
                      </span>
                      {r.status === "created" && r.is_new_contact === false && (
                        <span className="px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 text-[10px] font-semibold flex-shrink-0">Existing</span>
                      )}
                      {r.status === "created" && typeof r.amount === "number" && r.amount > 0 && (
                        <span className="text-slate-700 font-semibold tabular-nums flex-shrink-0">${r.amount.toLocaleString()}</span>
                      )}
                      <span className="text-slate-400 capitalize flex-shrink-0 min-w-[52px] text-right">{r.status}</span>
                    </div>
                    {r.status === "created" && (r.phone || r.email || r.address || r.quote_sent) && (
                      <div className="mt-1 ml-11 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
                        {r.phone && <span>{r.phone}</span>}
                        {r.email && <span className="truncate max-w-[220px]">{r.email}</span>}
                        {r.address && <span className="truncate max-w-[280px]">{r.address}</span>}
                        {r.quote_sent && <span className="text-slate-400">sent {r.quote_sent}</span>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {committed && (
                <div className="pt-2">
                  <Button variant="outline" onClick={() => navigate("/pipeline")} data-testid="import-view-pipeline">
                    View in Pipeline
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
